/**
 * App orchestrator
 * Handles UI component initialization and event binding
 */

import { HomeScreen } from '../../components/HomeScreen'
import { GameNavigation } from '../../components/GameNavigation'
import { Modal } from '../../components/Modal'
import { SettingsModal } from '../../components/SettingsModal'
import { DebugModal } from '../../components/DebugModal'
import { ConfirmationModal } from '../../components/ConfirmationModal'
import { ChaptersModal } from '../../components/ChaptersModal'
import { StoryNav } from '../../components/StoryNav'
import type { StoryNavUnits } from '../../components/StoryNav'
import { computeStoryUnits } from '../story-units'
import type { AppOrchestratorCallbacks } from './types'
import type storyData from '../../config/story.json'

type StoryData = typeof storyData

function getChaptersFromScenes(scenes: StoryData['scenes']): { id: string; title: string; sceneIndex: number }[] {
  return scenes
    .map((s, sceneIndex) =>
      s.type === 'chapter-title'
        ? { id: s.id, title: (s as { text?: string }).text ?? s.id, sceneIndex }
        : null
    )
    .filter((x): x is { id: string; title: string; sceneIndex: number } => x !== null)
}

function buildStoryNavUnits(scenes: StoryData['scenes']): StoryNavUnits {
  const chapters = getChaptersFromScenes(scenes)
  const chapterSceneIndices = chapters.map((c) => c.sceneIndex)
  const { totalSceneUnits, cumulativeUnitsToChapter } = computeStoryUnits(
    scenes as import('../story-units').SceneForUnits[],
    chapterSceneIndices
  )
  const chapterPositions = cumulativeUnitsToChapter.map((u) => u / Math.max(1, totalSceneUnits))
  return { totalSceneUnits, chapterPositions }
}

export interface AppUI {
  homeScreen: HomeScreen
  gameNavigation: GameNavigation
  settingsModal: SettingsModal
  debugModal: DebugModal
  confirmationModal: ConfirmationModal
  chaptersModal: ChaptersModal
  storyNav: StoryNav
}

/**
 * Initialize all UI components
 * @param app - Main app container
 * @param scenes - Story scenes data
 * @returns UI components
 */
export function initializeUI(app: HTMLElement, scenes: StoryData['scenes']): AppUI {
  const homeScreen = new HomeScreen(app)
  const gameNavigation = new GameNavigation(app)
  const modal = new Modal(app)
  const settingsModal = new SettingsModal(modal)
  const debugModal = new DebugModal(modal)
  debugModal.setScenes(scenes as any)
  const confirmationModal = new ConfirmationModal(modal)

  let storyNavContainer = app.querySelector<HTMLElement>('.story-nav')
  if (!storyNavContainer) {
    storyNavContainer = document.createElement('div')
    storyNavContainer.className = 'story-nav story-nav--hidden'
    storyNavContainer.setAttribute('aria-label', 'Navigace po kapitolách')
    app.insertBefore(storyNavContainer, app.firstChild)
  }
  const chapters = getChaptersFromScenes(scenes)
  const units = buildStoryNavUnits(scenes)
  const storyNav = new StoryNav(storyNavContainer, chapters, units)
  const chaptersModal = new ChaptersModal(
    modal,
    () => storyNav.getChapters(),
    () => storyNav.getRevealedChapterIndices(),
    (i) => storyNav.scrollToChapter(i)
  )
  storyNav.setOnOpenChaptersModal(() => chaptersModal.show())

  return {
    homeScreen,
    gameNavigation,
    settingsModal,
    debugModal,
    confirmationModal,
    chaptersModal,
    storyNav,
  }
}

/**
 * Setup all UI callbacks
 * @param ui - UI components
 * @param callbacks - Callback functions
 */
export function setupUICallbacks(ui: AppUI, callbacks: AppOrchestratorCallbacks): void {
  if (callbacks.onPlay) {
    ui.homeScreen.setOnPlay(callbacks.onPlay)
  }

  if (callbacks.onContinue) {
    ui.homeScreen.setOnContinue(callbacks.onContinue)
  }

  if (callbacks.onNewGame) {
    ui.homeScreen.setOnNewGame(callbacks.onNewGame)
  }

  if (callbacks.onBack) {
    ui.gameNavigation.setOnBack(callbacks.onBack)
  }

  if (callbacks.onDebug) {
    ui.gameNavigation.setOnDebug(callbacks.onDebug)
  }

  if (callbacks.onSettings) {
    ui.gameNavigation.setOnSettings(callbacks.onSettings)
  }

  if (callbacks.onChapter) {
    ui.gameNavigation.setOnChapter(callbacks.onChapter)
  }

  if (callbacks.onLoadGameState) {
    ui.debugModal.setOnLoadGameState(callbacks.onLoadGameState)
  }
}
